import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ArrowRight, Target, ListOrdered, Users, TrendingUp } from "lucide-react";

const FEATURED = [
  { name: "Premier League", slug: "premier-league-predictor" },
  { name: "La Liga", slug: "la-liga-predictor" },
  { name: "Serie A", slug: "serie-a-predictor" },
  { name: "UEFA Champions League", slug: "champions-league-predictor" },
];

const MORE = [
  { name: "Bundesliga", slug: "bundesliga-predictor" },
  { name: "Ligue 1", slug: "ligue-1-predictor" },
  { name: "UEFA Europa League", slug: "europa-league-predictor" },
  { name: "Eredivisie", slug: "eredivisie-predictor" },
  { name: "Saudi Pro League", slug: "saudi-pro-league-predictor" },
  { name: "Primeira Liga" },
  { name: "Süper Lig" },
  { name: "Super League Greece" },
];

export default function HowItWorks() {
  return (
    <div className="min-h-screen bg-background text-foreground grain">
      <Helmet>
        <title>How It Works — The Free Football Match Predictor | GAFFER</title>
        <meta name="description" content="GAFFER is a free football score predictor: call every scoreline week by week, build your predicted table, climb the worldwide leaderboard and beat your mates in private banter leagues. Here's how it works." />
        <link rel="canonical" href="https://thefootballgaffer.com/how-it-works" />
      </Helmet>
      <header className="border-b-2 border-foreground">
        <div className="max-w-3xl mx-auto px-4 py-5 flex items-center justify-between">
          <Link to="/" className="font-display text-3xl tracking-tight leading-none">GAFFER<span className="text-primary">.</span></Link>
          <Link to="/register"><Button size="sm" className="rounded-none bg-primary text-primary-foreground uppercase font-bold" data-testid="hiw-signup-btn">Play free <ArrowRight size={14} className="ms-1" /></Button></Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12" data-testid="how-it-works">
        <p className="overline text-primary mb-2">How it works</p>
        <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none mb-4">The free football match predictor for real fans</h1>
        <p className="text-base text-muted-foreground mb-10">
          GAFFER is a free football score predictor where you call the scoreline of every match, week by week,
          and watch your own predicted league table take shape. Earn points for accuracy, climb the worldwide
          rankings, and take on your mates in private banter leagues. No guesswork — just proper football knowledge.
        </p>

        <section className="space-y-8">
          <div>
            <h2 className="font-display text-2xl uppercase flex items-center gap-2 mb-2"><Target size={20} className="text-primary" /> 1. Predict every scoreline</h2>
            <p className="text-muted-foreground">Each matchday you predict the exact score of every fixture. Nail the exact score for
            maximum points, get the result and goal difference right for partial points. Real bookmaker win
            probabilities help you weigh up each game before you commit.</p>
          </div>
          <div>
            <h2 className="font-display text-2xl uppercase flex items-center gap-2 mb-2"><ListOrdered size={20} className="text-primary" /> 2. Build your predicted table</h2>
            <p className="text-muted-foreground">Your predictions feed a live predicted league table, so you can see exactly where you
            think each club finishes the season — and share a shareable table card with friends.</p>
          </div>
          <div>
            <h2 className="font-display text-2xl uppercase flex items-center gap-2 mb-2"><TrendingUp size={20} className="text-primary" /> 3. Climb the worldwide leaderboard</h2>
            <p className="text-muted-foreground">Points are ranked on a global accuracy leaderboard, tie-broken by exact correct scores.
            See how your football brain stacks up against fans everywhere.</p>
          </div>
          <div>
            <h2 className="font-display text-2xl uppercase flex items-center gap-2 mb-2"><Users size={20} className="text-primary" /> 4. Start a banter league</h2>
            <p className="text-muted-foreground">Create a private mini-league and invite your mates. Settle who really knows their football
            with head-to-head rivalries and season-long bragging rights.</p>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl uppercase mb-3">Leagues we cover</h2>
          <p className="text-muted-foreground mb-4">Predict across Europe's biggest competitions and beyond — start with the big four:</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            {FEATURED.map((l) => (
              <Link key={l.name} to={`/predictor/${l.slug}`} data-testid={`hiw-featured-${l.slug}`}
                className="group flex items-center justify-between border-2 border-primary bg-primary/10 px-3 py-3 text-sm font-display uppercase leading-tight tracking-tight hover:bg-primary hover:text-primary-foreground transition-colors">
                <span>{l.name}</span>
                <ArrowRight size={14} className="shrink-0 opacity-70 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {MORE.map((l) => (
              l.slug ? (
                <li key={l.name}>
                  <Link to={`/predictor/${l.slug}`} data-testid={`hiw-more-${l.slug}`}
                    className="block border border-border px-3 py-2 text-sm font-data hover:border-primary hover:text-primary transition-colors">{l.name}</Link>
                </li>
              ) : (
                <li key={l.name} className="border border-border px-3 py-2 text-sm font-data text-muted-foreground">{l.name}</li>
              )
            ))}
          </ul>
        </section>

        <section className="mt-12 border-2 border-foreground p-6 text-center">
          <h2 className="font-display text-3xl uppercase mb-2">Think you can call it?</h2>
          <p className="text-muted-foreground mb-4">GAFFER is completely free to play. Make your first predictions in under a minute.</p>
          <Link to="/register"><Button className="rounded-none bg-primary text-primary-foreground uppercase font-bold" data-testid="hiw-cta-btn">Start predicting <ArrowRight size={15} className="ms-1" /></Button></Link>
        </section>

        <footer className="mt-12 pt-6 border-t border-border text-xs text-muted-foreground font-data">
          <Link to="/" className="hover:text-primary">← Back to home</Link>
        </footer>
      </main>
    </div>
  );
}
